"use client";

import Link from "next/link";
import { Eye, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { resolveShowcaseTool } from "@/components/showcase/showcaseMeta";
import { ShowcaseDeleteButton } from "@/components/showcase/ShowcaseDeleteButton";
import type { ShowcaseItem } from "@/hooks/useShowcaseApi";

/** Subtle checkerboard so transparent PNGs read clearly. */
const CHECKERBOARD =
  "bg-[length:16px_16px] bg-[linear-gradient(45deg,hsl(var(--muted))_25%,transparent_25%),linear-gradient(-45deg,hsl(var(--muted))_25%,transparent_25%),linear-gradient(45deg,transparent_75%,hsl(var(--muted))_75%),linear-gradient(-45deg,transparent_75%,hsl(var(--muted))_75%)] bg-[position:0_0,0_8px,8px_-8px,-8px_0px]";

export function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${Math.round(n / 1000)}k`;
}

export function ShowcaseCard({ item, className }: { item: ShowcaseItem; className?: string }) {
  const tool = resolveShowcaseTool(item.source_tool);
  const ToolIcon = tool.icon;

  return (
    <Link
      href={`/showcase/${item.id}`}
      className={cn(
        "group flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/80 transition duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-sm hover:shadow-accent/10",
        className,
      )}
    >
      <div className={cn("relative aspect-square w-full overflow-hidden", CHECKERBOARD)}>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={item.image_url}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
        />
        {item.kind === "model" ? (
          <span className="absolute left-2 top-2 rounded-full border border-white/20 bg-black/55 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-white/90">
            3D
          </span>
        ) : null}
        <ShowcaseDeleteButton item={item} variant="overlay" />
      </div>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <p className="line-clamp-1 text-sm font-semibold tracking-tight text-foreground">{item.title}</p>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <ToolIcon className="h-3.5 w-3.5 text-accent" />
          <span className="truncate">{tool.title}</span>
        </div>
        <div className="mt-auto flex items-center justify-between pt-1 text-[11px] text-muted-foreground">
          <span className="truncate">by {item.creator_handle}</span>
          <span className="flex shrink-0 items-center gap-2.5">
            <span className="inline-flex items-center gap-1">
              <Heart className="h-3.5 w-3.5" />
              {formatCount(item.like_count)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Eye className="h-3.5 w-3.5" />
              {formatCount(item.view_count)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}
